import { useEffect, useRef } from "react";
import { toast } from "react-toastify";

const VERSION_KEY = "erp_app_version";

const useCheckForUpdate = () => {
  const currentVersionRef = useRef(null);
  const isReloadingRef = useRef(false);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    let intervalId;

    const fetchMeta = async () => {
      // Don't check again if we are already reloading or a fetch is in progress
      if (isReloadingRef.current || isFetchingRef.current) return;
      isFetchingRef.current = true;

      try {
        const response = await fetch(`/meta.json?t=${Date.now()}`);
        if (!response.ok) return;
        const meta = await response.json();
        const serverVersion = meta.version;

        const storedVersion = localStorage.getItem(VERSION_KEY);

        if (!storedVersion) {
          // First ever visit — store the version and set in-memory ref
          localStorage.setItem(VERSION_KEY, serverVersion);
          currentVersionRef.current = serverVersion;
        } else if (serverVersion > Number(storedVersion)) {
          // New version detected — update storage then reload
          localStorage.setItem(VERSION_KEY, serverVersion);
          currentVersionRef.current = serverVersion;
          isReloadingRef.current = true;

          // Show toast notification
          toast.info("New version detected. loading in 3 sec", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });

          // Set flag so the service worker's controllerchange handler
          // knows not to trigger a second reload
          sessionStorage.setItem("erp_sw_reload_pending", "1");
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        } else {
          // Same version — keep in-memory ref in sync for periodic checks
          currentVersionRef.current = serverVersion;
        }
      } catch (error) {
        // Ignore fetch errors to avoid spamming the console for offline users
        // console.error("Error checking for updates:", error);
      } finally {
        isFetchingRef.current = false;
      }
    };

    // Check immediately on mount
    fetchMeta();

    // Check periodically (e.g., every 5 minutes = 300,000ms)
    intervalId = setInterval(fetchMeta, 300000);

    // Also check when the user switches back to the tab
    const handleFocus = () => {
      fetchMeta();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchMeta();
      }
    };
    window.addEventListener("focus", handleFocus);
    window.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};

export default useCheckForUpdate;
